/**
 * Custom HTML blocks embed /api/v1/media/:id/serve paths in <img src>.
 *
 * Production gotchas:
 * 1. Migration 171 requires ?t=<tenant-slug> (or X-Tenant-ID) on serve URLs.
 * 2. The storefront SPA has no /api proxy in production — relative /api/v1/...
 *    paths hit Vercel and return HTML 404, not image bytes.
 * 3. Site Builder often stores ABSOLUTE urls (https://api.busmgr.com/api/v1/media/…).
 *    A substring replace on those produces garbage URLs; always extract the media id
 *    and rebuild from the configured apiBase.
 */

// Query capture must NOT use \\s inside [] — that excludes the literal letter "s".
const MEDIA_SERVE_PATH_RE = /(?:\/api\/v1)?\/media\/([0-9a-f-]{36})\/serve(?:\?([^"'\\>]*))?/i;

function normalizeApiBase(apiBase = '/api/v1') {
  if (!apiBase || apiBase === '/api/v1') return '/api/v1';
  return String(apiBase).replace(/\/$/, '');
}

function buildMediaServeUrl(id, queryString, tenantSlug, apiBase) {
  let qs = queryString || '';
  if (tenantSlug && !/(^|&)t=/.test(qs)) {
    const tenantParam = `t=${encodeURIComponent(tenantSlug)}`;
    qs = qs ? `${qs}&${tenantParam}` : tenantParam;
  }
  const base = normalizeApiBase(apiBase);
  return `${base}/media/${id}/serve${qs ? `?${qs}` : ''}`;
}

export function rewriteMediaServeUrls(html, tenantSlug, apiBase = '/api/v1') {
  if (!html) return html;

  return html.replace(
    /(?:\/api\/v1)?\/media\/([0-9a-f-]{36})\/serve(?:\?([^"'\\>]*))?/gi,
    (_match, id, queryString) => buildMediaServeUrl(id, queryString, tenantSlug, apiBase),
  );
}

/** Rewrite a single media serve URL (img src, CSS background-image, etc.). */
export function resolveMediaServeUrl(url, tenantSlug, apiBase = '/api/v1') {
  if (!url || typeof url !== 'string') return url;
  const match = url.match(MEDIA_SERVE_PATH_RE);
  if (!match) return url;
  return buildMediaServeUrl(match[1], match[2], tenantSlug, apiBase);
}

/** Gallery blocks store { src, alt, caption } or plain strings. */
export function resolveGalleryImageSrc(img, tenantSlug, apiBase = '/api/v1') {
  const raw = typeof img === 'string' ? img : img?.src;
  if (!raw) return '';
  return resolveMediaServeUrl(raw, tenantSlug, apiBase) || raw;
}

/** @deprecated Use rewriteMediaServeUrls */
export function injectMediaTenantParam(html, tenantSlug) {
  return rewriteMediaServeUrls(html, tenantSlug);
}
